import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  MapPin,
  Car,
  Building2,
  ChevronDown,
  ChevronUp,
  Copy,
  Check,
  Euro,
  AlertCircle,
  Footprints,
  Route,
  Zap,
  BatteryCharging,
} from 'lucide-react';
import { MapEmbed } from '@/components/ui/MapEmbed';
import { formatCurrency } from '@/lib/currency';
import type { ParkingType, EVChargerType } from '@/types/database';

interface ArrivalNavigationCardProps {
  // Property location
  propertyAddress: string;
  propertyCity: string;
  propertyLatitude: number | null;
  propertyLongitude: number | null;
  // Parking info
  parkingAvailable: boolean;
  parkingType?: ParkingType | null;
  parkingFree?: boolean;
  parkingPricePerDay?: number | null;
  parkingLatitude?: number | null;
  parkingLongitude?: number | null;
  parkingAddress?: string | null;
  parkingInstructions?: string | null;
  parkingAfterArrival?: string | null;
  parkingSpots?: number;
  parkingVehicleRestrictions?: string | null;
  parkingNotes?: string | null;
  // EV Charger info
  evChargerAvailable?: boolean;
  evChargerType?: EVChargerType | null;
  evChargerPowerKw?: number | null;
  evChargerInstructions?: string | null;
  // Styling
  darkMode: boolean;
  currency?: string;
}

export function ArrivalNavigationCard({
  propertyAddress,
  propertyCity,
  propertyLatitude,
  propertyLongitude,
  parkingAvailable,
  parkingType,
  parkingFree,
  parkingPricePerDay,
  parkingLatitude,
  parkingLongitude,
  parkingAddress,
  parkingInstructions,
  parkingAfterArrival,
  parkingSpots = 1,
  parkingVehicleRestrictions,
  parkingNotes,
  evChargerAvailable = false,
  evChargerType,
  evChargerPowerKw,
  evChargerInstructions,
  darkMode,
  currency,
}: ArrivalNavigationCardProps) {
  const { t } = useTranslation();
  const [showParkingDetails, setShowParkingDetails] = useState(false);
  const [copiedAddress, setCopiedAddress] = useState(false);

  // Check if parking is at a different location
  const hasSeparateParking =
    parkingAvailable &&
    parkingLatitude &&
    parkingLongitude &&
    (parkingLatitude !== propertyLatitude || parkingLongitude !== propertyLongitude);

  // Determine which coordinates to show on map (property or parking if separate)
  const mapLat = hasSeparateParking ? parkingLatitude : propertyLatitude;
  const mapLng = hasSeparateParking ? parkingLongitude : propertyLongitude;
  const hasMapCoordinates = mapLat && mapLng;

  const [showNavOptions, setShowNavOptions] = useState<'property' | 'parking' | null>(null);

  const getNavigationLinks = (lat: number | null, lng: number | null, address: string) => {
    const links: { name: string; icon: string; url: string }[] = [];

    if (lat && lng) {
      links.push({
        name: 'Google Maps',
        icon: '🗺️',
        url: `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}&travelmode=driving`,
      });
      links.push({
        name: 'Waze',
        icon: '🚗',
        url: `https://waze.com/ul?ll=${lat},${lng}&navigate=yes`,
      });
      links.push({
        name: 'Apple Maps',
        icon: '🍎',
        url: `https://maps.apple.com/?daddr=${lat},${lng}&dirflg=d`,
      });
    } else {
      const encoded = encodeURIComponent(address);
      links.push({
        name: 'Google Maps',
        icon: '🗺️',
        url: `https://www.google.com/maps/dir/?api=1&destination=${encoded}&travelmode=driving`,
      });
      links.push({
        name: 'Waze',
        icon: '🚗',
        url: `https://waze.com/ul?q=${encoded}&navigate=yes`,
      });
      links.push({
        name: 'Apple Maps',
        icon: '🍎',
        url: `https://maps.apple.com/?daddr=${encoded}&dirflg=d`,
      });
    }

    return links;
  };

  const navigateToProperty = () => {
    setShowNavOptions(showNavOptions === 'property' ? null : 'property');
  };

  const navigateToParking = () => {
    setShowNavOptions(showNavOptions === 'parking' ? null : 'parking');
  };

  // No trailing ", " when the city is missing (or already in the address).
  const fullPropertyAddress = [propertyAddress, propertyCity]
    .filter((p, i, arr) => !!p && (i === 0 || !arr[0]?.includes(p)))
    .join(', ');

  const propertyNavLinks = getNavigationLinks(
    propertyLatitude,
    propertyLongitude,
    fullPropertyAddress
  );

  const parkingNavLinks = getNavigationLinks(
    parkingLatitude || null,
    parkingLongitude || null,
    parkingAddress || ''
  );

  const copyAddress = async (address: string) => {
    try {
      await navigator.clipboard.writeText(address);
      setCopiedAddress(true);
      setTimeout(() => setCopiedAddress(false), 2000);
    } catch {
      // Clipboard unavailable (in-app browser / insecure context) — ignore.
    }
  };

  // Get parking type label
  const getParkingTypeInfo = (type: ParkingType | null | undefined) => {
    switch (type) {
      case 'private':
        return { icon: '🔒', label: t('guest.parking.types.private', 'Privatni parking') };
      case 'public':
        return { icon: '🅿️', label: t('guest.parking.types.public', 'Javni parking') };
      case 'street':
        return { icon: '🛣️', label: t('guest.parking.types.street', 'Ulični parking') };
      case 'garage':
        return { icon: '🏢', label: t('guest.parking.types.garage', 'Garaža') };
      default:
        return { icon: '🚗', label: t('guest.parking.title', 'Parking') };
    }
  };

  const parkingTypeInfo = getParkingTypeInfo(parkingType);

  return (
    <div
      className={`relative overflow-hidden rounded-2xl transition-all ${
        darkMode
          ? 'bg-slate-800 border border-slate-700 shadow-xl shadow-black/40 ring-1 ring-white/5'
          : 'bg-gradient-to-br from-white to-sky-50/30 border border-sky-100 shadow-xl shadow-gray-200/80'
      }`}
    >
      {/* Decorative gradient */}
      <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-br from-sky-500/10 to-blue-500/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />

      <div className="relative p-5">
        {/* Header — strong text title (no icon badge; group icon is above). */}
        <div className={`flex items-center gap-3 mb-4 pb-3 border-b ${darkMode ? 'border-slate-700' : 'border-gray-100'}`}>
          <div>
            <h3
              className={`font-bold text-lg tracking-tight ${
                darkMode ? 'text-white' : 'text-gray-900'
              }`}
            >
              {t('guest.arrival.title', 'Navigacija')}
            </h3>
            <p
              className={`text-xs ${
                darkMode ? 'text-slate-400' : 'text-gray-500'
              }`}
            >
              {t('guest.arrival.subtitle', 'Otvori u Google Maps')}
            </p>
          </div>
        </div>

        {/* Map Preview */}
        {hasMapCoordinates && (
          <div className="mb-4">
            <MapEmbed
              latitude={mapLat}
              longitude={mapLng}
              title={hasSeparateParking ? (parkingAddress || t('guest.parking.title')) : propertyAddress}
              zoom={15}
              height="140px"
              darkMode={darkMode}
              showOpenButton={false}
            />
          </div>
        )}

        {/* Navigation Buttons */}
        <div className="space-y-3">
          {/* Navigate to Property */}
          <div>
            <button
              onClick={navigateToProperty}
              className={`w-full flex items-center gap-4 p-4 rounded-xl transition-all duration-200 group ${
                darkMode
                  ? 'bg-gradient-to-r from-sky-500/10 to-blue-500/10 hover:from-sky-500/20 hover:to-blue-500/20 border border-sky-500/20'
                  : 'bg-gradient-to-r from-sky-50 to-blue-50 hover:from-sky-100 hover:to-blue-100 border border-sky-200/50'
              }`}
            >
              <div
                className={`shrink-0 p-3 rounded-xl transition-all group-hover:scale-110 ${
                  darkMode ? 'bg-sky-500/20' : 'bg-white shadow-sm'
                }`}
              >
                <Building2
                  className={`w-6 h-6 ${darkMode ? 'text-sky-400' : 'text-sky-600'}`}
                />
              </div>
              <div className="flex-1 min-w-0 text-left">
                <p className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  {t('guest.arrival.toProperty', 'Navigiraj do smještaja')}
                </p>
                <p className={`text-sm truncate ${darkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                  {propertyAddress}
                </p>
              </div>
              <div
                className={`shrink-0 p-2 rounded-lg transition-all ${
                  darkMode ? 'text-sky-400' : 'text-sky-600'
                }`}
              >
                <ChevronDown className={`w-5 h-5 transition-transform ${showNavOptions === 'property' ? 'rotate-180' : ''}`} />
              </div>
            </button>
            {/* Navigation app options */}
            {showNavOptions === 'property' && (
              <div className={`mt-2 grid grid-cols-3 gap-2`}>
                {propertyNavLinks.map((link) => (
                  <a
                    key={link.name}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`flex flex-col items-center gap-1.5 p-3 rounded-xl transition-all hover:scale-105 active:scale-95 ${
                      darkMode
                        ? 'bg-slate-700/50 hover:bg-slate-700'
                        : 'bg-white hover:bg-gray-50 shadow-sm border border-gray-100'
                    }`}
                  >
                    <span className="text-xl">{link.icon}</span>
                    <span className={`text-[10px] font-medium ${darkMode ? 'text-slate-300' : 'text-gray-600'}`}>
                      {link.name}
                    </span>
                  </a>
                ))}
              </div>
            )}
          </div>

          {/* Navigate to Parking (if different location or available) */}
          {parkingAvailable && (hasSeparateParking || parkingAddress) && (
            <div>
              <button
                onClick={navigateToParking}
                className={`w-full flex items-center gap-4 p-4 rounded-xl transition-all duration-200 group ${
                  darkMode
                    ? 'bg-gradient-to-r from-amber-500/10 to-orange-500/10 hover:from-amber-500/20 hover:to-orange-500/20 border border-amber-500/20'
                    : 'bg-gradient-to-r from-amber-50 to-orange-50 hover:from-amber-100 hover:to-orange-100 border border-amber-200/50'
                }`}
              >
                <div
                  className={`shrink-0 p-3 rounded-xl transition-all group-hover:scale-110 ${
                    darkMode ? 'bg-amber-500/20' : 'bg-white shadow-sm'
                  }`}
                >
                  <Car className={`w-6 h-6 ${darkMode ? 'text-amber-400' : 'text-amber-600'}`} />
                </div>
                <div className="flex-1 min-w-0 text-left">
                  <p className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    {t('guest.arrival.toParking', 'Navigiraj do parkinga')}
                  </p>
                  <p className={`text-sm truncate ${darkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                    {parkingAddress || t('guest.arrival.separateLocation', 'Zasebna lokacija')}
                  </p>
                </div>
                <div
                  className={`shrink-0 p-2 rounded-lg transition-all ${
                    darkMode ? 'text-amber-400' : 'text-amber-600'
                  }`}
                >
                  <ChevronDown className={`w-5 h-5 transition-transform ${showNavOptions === 'parking' ? 'rotate-180' : ''}`} />
                </div>
              </button>
              {showNavOptions === 'parking' && (
                <div className={`mt-2 grid grid-cols-3 gap-2`}>
                  {parkingNavLinks.map((link) => (
                    <a
                      key={link.name}
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`flex flex-col items-center gap-1.5 p-3 rounded-xl transition-all hover:scale-105 active:scale-95 ${
                        darkMode
                          ? 'bg-slate-700/50 hover:bg-slate-700'
                          : 'bg-white hover:bg-gray-50 shadow-sm border border-gray-100'
                      }`}
                    >
                      <span className="text-xl">{link.icon}</span>
                      <span className={`text-[10px] font-medium ${darkMode ? 'text-slate-300' : 'text-gray-600'}`}>
                        {link.name}
                      </span>
                    </a>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Path from Parking to Entrance - Always visible if available */}
        {parkingAfterArrival && (
          <div
            className={`mt-4 p-4 rounded-xl ${
              darkMode
                ? 'bg-gradient-to-r from-emerald-500/10 to-teal-500/10 border border-emerald-500/20'
                : 'bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-100'
            }`}
          >
            <div className="flex items-start gap-3">
              <div
                className={`p-2 rounded-lg ${
                  darkMode ? 'bg-emerald-500/20' : 'bg-white shadow-sm'
                }`}
              >
                <Footprints className={`w-5 h-5 ${darkMode ? 'text-emerald-400' : 'text-emerald-600'}`} />
              </div>
              <div className="flex-1">
                <p className={`text-sm font-semibold mb-1 ${darkMode ? 'text-emerald-300' : 'text-emerald-800'}`}>
                  {t('guest.arrival.pathToEntrance', 'Put do ulaza')}
                </p>
                <p className={`text-sm whitespace-pre-wrap ${darkMode ? 'text-slate-300' : 'text-gray-700'}`}>
                  {parkingAfterArrival}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Parking Details Section */}
        {parkingAvailable && (
          <div className={`mt-4 pt-4 border-t ${darkMode ? 'border-slate-700/50' : 'border-gray-100'}`}>
            {/* Parking Header */}
            <button
              onClick={() => setShowParkingDetails(!showParkingDetails)}
              className="w-full flex items-center justify-between"
            >
              <div className="flex items-center gap-3">
                <span className="text-2xl">{parkingTypeInfo.icon}</span>
                <div className="text-left">
                  <p className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    {t('guest.parking.title', 'Parking')}
                  </p>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-sm ${darkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                      {parkingTypeInfo.label}
                    </span>
                    {parkingFree === true ? (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-green-500/20 text-green-500 font-medium">
                        {t('guest.parking.free', 'Besplatno')}
                      </span>
                    ) : parkingPricePerDay ? (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-500 font-medium">
                        {formatCurrency(parkingPricePerDay, currency)}/{t('common.day', 'dan')}
                      </span>
                    ) : null}
                    {!!parkingSpots && parkingSpots > 1 && (
                      <span className={`text-xs ${darkMode ? 'text-slate-500' : 'text-gray-400'}`}>
                        • {parkingSpots} {t('guest.parking.spots', 'mjesta')}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className={`p-2 rounded-full ${darkMode ? 'hover:bg-slate-700' : 'hover:bg-gray-100'}`}>
                {showParkingDetails ? (
                  <ChevronUp className={`w-5 h-5 ${darkMode ? 'text-slate-400' : 'text-gray-400'}`} />
                ) : (
                  <ChevronDown className={`w-5 h-5 ${darkMode ? 'text-slate-400' : 'text-gray-400'}`} />
                )}
              </div>
            </button>

            {/* Expanded Parking Details */}
            {showParkingDetails && (
              <div className="mt-4 space-y-3">
                {/* Parking Address with Copy */}
                {parkingAddress && (
                  <div
                    className={`p-3 rounded-xl flex items-center justify-between ${
                      darkMode ? 'bg-slate-700/50' : 'bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <MapPin className={`w-4 h-4 ${darkMode ? 'text-blue-400' : 'text-blue-500'}`} />
                      <span className={`text-sm ${darkMode ? 'text-slate-300' : 'text-gray-600'}`}>
                        {parkingAddress}
                      </span>
                    </div>
                    <button
                      onClick={() => copyAddress(parkingAddress)}
                      className={`p-2 rounded-lg transition-colors ${
                        darkMode ? 'hover:bg-slate-600' : 'hover:bg-gray-200'
                      }`}
                    >
                      {copiedAddress ? (
                        <Check className="w-4 h-4 text-green-500" />
                      ) : (
                        <Copy className={`w-4 h-4 ${darkMode ? 'text-slate-400' : 'text-gray-400'}`} />
                      )}
                    </button>
                  </div>
                )}

                {/* How to get there */}
                {parkingInstructions && (
                  <div
                    className={`p-4 rounded-xl ${
                      darkMode
                        ? 'bg-blue-500/10 border border-blue-500/20'
                        : 'bg-blue-50 border border-blue-100'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <Route className={`w-5 h-5 mt-0.5 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`} />
                      <div>
                        <p className={`text-sm font-medium mb-2 ${darkMode ? 'text-blue-300' : 'text-blue-800'}`}>
                          {t('guest.parking.howToGetThere', 'Kako doći')}
                        </p>
                        <p className={`text-sm whitespace-pre-wrap ${darkMode ? 'text-slate-300' : 'text-gray-700'}`}>
                          {parkingInstructions}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* After arrival */}
                {parkingAfterArrival && (
                  <div
                    className={`p-4 rounded-xl ${
                      darkMode
                        ? 'bg-green-500/10 border border-green-500/20'
                        : 'bg-green-50 border border-green-100'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <Footprints className={`w-5 h-5 mt-0.5 ${darkMode ? 'text-green-400' : 'text-green-600'}`} />
                      <div>
                        <p className={`text-sm font-medium mb-2 ${darkMode ? 'text-green-300' : 'text-green-800'}`}>
                          {t('guest.parking.afterArrival', 'Nakon dolaska')}
                        </p>
                        <p className={`text-sm whitespace-pre-wrap ${darkMode ? 'text-slate-300' : 'text-gray-700'}`}>
                          {parkingAfterArrival}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Vehicle Restrictions */}
                {parkingVehicleRestrictions && (
                  <div
                    className={`p-4 rounded-xl ${
                      darkMode
                        ? 'bg-amber-500/10 border border-amber-500/20'
                        : 'bg-amber-50 border border-amber-100'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <AlertCircle className={`w-5 h-5 mt-0.5 ${darkMode ? 'text-amber-400' : 'text-amber-600'}`} />
                      <div>
                        <p className={`text-sm font-medium mb-1 ${darkMode ? 'text-amber-300' : 'text-amber-800'}`}>
                          {t('guest.parking.vehicleRestrictions', 'Ograničenja')}
                        </p>
                        <p className={`text-sm ${darkMode ? 'text-slate-300' : 'text-gray-700'}`}>
                          {parkingVehicleRestrictions}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Additional Notes */}
                {parkingNotes && (
                  <div className={`pt-3 border-t ${darkMode ? 'border-slate-700/50' : 'border-gray-100'}`}>
                    <p className={`text-xs mb-1 ${darkMode ? 'text-slate-500' : 'text-gray-400'}`}>
                      {t('guest.parking.additionalNotes', 'Napomene')}
                    </p>
                    <p className={`text-sm ${darkMode ? 'text-slate-300' : 'text-gray-600'}`}>
                      {parkingNotes}
                    </p>
                  </div>
                )}

                {/* Price Info */}
                {parkingFree !== true && !!parkingPricePerDay && (
                  <div
                    className={`flex items-center gap-2 p-3 rounded-xl ${
                      darkMode ? 'bg-slate-700/50' : 'bg-gray-50'
                    }`}
                  >
                    <Euro className={`w-4 h-4 ${darkMode ? 'text-amber-400' : 'text-amber-500'}`} />
                    <span className={`text-sm ${darkMode ? 'text-slate-300' : 'text-gray-600'}`}>
                      {t('guest.parking.priceInfo', '{{formattedPrice}} po danu', { formattedPrice: formatCurrency(parkingPricePerDay, currency) })}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* EV Charger Section */}
        {evChargerAvailable && (
          <div className={`mt-4 pt-4 border-t ${darkMode ? 'border-slate-700/50' : 'border-gray-100'}`}>
            <div className="flex items-center gap-3">
              <div
                className={`p-2 rounded-xl ${
                  darkMode ? 'bg-green-500/20' : 'bg-green-100'
                }`}
              >
                <Zap className={`w-5 h-5 ${darkMode ? 'text-green-400' : 'text-green-600'}`} />
              </div>
              <div className="flex-1">
                <p className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  {t('guest.evCharger.title', 'EV punjač dostupan')}
                </p>
                <div className="flex items-center gap-2 flex-wrap mt-1">
                  {evChargerType && (
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                      darkMode ? 'bg-green-500/20 text-green-400' : 'bg-green-100 text-green-700'
                    }`}>
                      {t(`units.form.evCharger.types.${evChargerType}`, evChargerType.toUpperCase())}
                    </span>
                  )}
                  {!!evChargerPowerKw && (
                    <span className={`flex items-center gap-1 text-xs ${darkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                      <BatteryCharging className="w-3 h-3" />
                      {evChargerPowerKw} kW
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* EV Charger Instructions */}
            {evChargerInstructions && (
              <div
                className={`mt-3 p-3 rounded-xl ${
                  darkMode
                    ? 'bg-green-500/10 border border-green-500/20'
                    : 'bg-green-50 border border-green-100'
                }`}
              >
                <p className={`text-sm whitespace-pre-wrap ${darkMode ? 'text-slate-300' : 'text-gray-700'}`}>
                  {evChargerInstructions}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Helpful tip */}
        <p
          className={`mt-4 text-xs text-center ${
            darkMode ? 'text-slate-500' : 'text-gray-400'
          }`}
        >
          {t('guest.arrival.tip', 'Dodirnite za navigaciju s uputama')} 🚗
        </p>
      </div>
    </div>
  );
}

export default ArrivalNavigationCard;
